import { CandidateProfileResponseObject } from 'web-library/models/AccountManagementConfiguration';
import dayjs from 'dayjs';
import { UseFormReturn } from 'react-hook-form';
import { PAYLOAD_DATE_FORMAT } from 'web-library';
import { useMemo } from 'react';
import { IExamContentResponse } from '../../../api-refactored/user/useGetIExamMasterCode';
import { IExamMasterCodeItem, LookupListItem } from '../../../api/user/model';
import { CreateEditCandidateSchemaData } from './CreateEditCandidateSchema';

interface useCreateEditViewCandidateAccountDetailsProps {
  iExamsData?: IExamContentResponse;
  candidateProfileDetails?: CandidateProfileResponseObject;
  dataOrganisation?: LookupListItem[];
  isTaskDetails?: boolean;
  isEdit?: boolean;
  isEditState?: boolean;
  isApproverNeededAction?: boolean;
  schoolDefaultObject?: LookupListItem;
  formHook?: UseFormReturn<CreateEditCandidateSchemaData>;
  uuid?: string;
  fileKeys?: string[];
}

export const useCandidatePersonalParticulars = ({
  iExamsData,
  candidateProfileDetails,
  dataOrganisation,
  isTaskDetails,
  isEdit,
  isEditState,
  isApproverNeededAction,
}: useCreateEditViewCandidateAccountDetailsProps) => {
  const defaultDateOfBirth =
    candidateProfileDetails &&
    dayjs
      .unix(candidateProfileDetails?.dob / 1000)
      .utc()
      .utcOffset(8)
      .format(PAYLOAD_DATE_FORMAT);
  const injectedOrganisationList = useMemo(() => {
    if (!dataOrganisation || !candidateProfileDetails?.schoolUuid) return dataOrganisation;

    const existingSchool = dataOrganisation.find(
      (org) => org.uuid === candidateProfileDetails.schoolUuid
    );

    // If school is already present, no need to inject
    if (existingSchool) return dataOrganisation;

    // Otherwise, inject candidate's current MOE school
    const injectedSchool: LookupListItem = {
      id:
        typeof candidateProfileDetails.schoolCode === 'string'
          ? parseInt(candidateProfileDetails.schoolCode, 10)
          : candidateProfileDetails.schoolCode ?? 0,
      uuid: candidateProfileDetails.schoolUuid,
      name: candidateProfileDetails.school,
    };

    return [injectedSchool, ...dataOrganisation];
  }, [
    dataOrganisation,
    candidateProfileDetails?.schoolUuid,
    candidateProfileDetails?.school,
    candidateProfileDetails?.schoolCode,
  ]);

  const genderDefaultObject = useMemo(
    () =>
      iExamsData?.gender.find(
        (obj) => obj.codeValueDescription === candidateProfileDetails?.gender.toUpperCase()
      ),
    [candidateProfileDetails?.gender, iExamsData?.gender]
  );

  const raceDefaultObject = useMemo(
    () =>
      iExamsData?.race.find(
        (obj) => obj.codeValueDescription === candidateProfileDetails?.race.toUpperCase()
      ),
    [candidateProfileDetails?.race, iExamsData?.race]
  );

  const occupationDefaultObject = useMemo(
    () =>
      iExamsData?.occupation.find((obj) => obj.description === candidateProfileDetails?.occupation),
    [candidateProfileDetails?.occupation, iExamsData?.occupation]
  );

  const schoolDefaultObject = useMemo(
    () =>
      injectedOrganisationList &&
      injectedOrganisationList.find((obj) => obj.uuid === candidateProfileDetails?.schoolUuid),
    [candidateProfileDetails?.schoolUuid, injectedOrganisationList]
  );

  const IsPersonalParticularsValueUndefined =
    !isTaskDetails &&
    ((candidateProfileDetails?.gender && genderDefaultObject === undefined) ||
      (candidateProfileDetails?.occupation && occupationDefaultObject === undefined) ||
      (candidateProfileDetails?.candidateType &&
        candidateProfileDetails?.candidateType !== 'S' &&
        candidateProfileDetails?.school &&
        schoolDefaultObject === undefined) ||
      (candidateProfileDetails?.race && raceDefaultObject === undefined));

  const filteredDataOrganisationWithoutMOESchool = useMemo(
    () =>
      injectedOrganisationList &&
      injectedOrganisationList.filter((organisation) => organisation.name !== 'MOE School'),
    [injectedOrganisationList]
  );

  const isMOESchool = schoolDefaultObject && schoolDefaultObject.name === 'MOE School';

  const disabledFields = useMemo(
    () => ({
      action: isEditState,
      approver: !isApproverNeededAction && !isEditState,
      remarks: !isApproverNeededAction && !isEditState,
      name: !isEditState,
      hanyupinyinName: !isEditState,
      IdType: !isEditState,
      NricFin: isEdit,
      school: !isEditState,
      gender: !isEditState,
      race: !isEditState,
      citizenship: true,
      nationality: !isEditState,
      prStatus: !isEditState,
      dob: !isEditState,
      email: !isEditState,
      mobileCountryCode: !isEditState,
      mobileContactNumber: !isEditState,
      telephoneCountryCode: !isEditState,
      telephoneContactNumber: !isEditState,
      preferredTelephoneOrMobile: !isEditState,
      houseOrBlkNo: !isEditState,
      streetName: !isEditState,
      postalCode: !isEditState,
      preferredAddress: !isEditState,
      foreignAddress1: !isEditState,
      foreignAddress2: !isEditState,
      foreignAddress3: !isEditState,
      nextOfKinName: !isEditState,
      nextOfKinContactNumber: !isEditState,
      nextOfKinRelationshipToCandidate: !isEditState,
    }),
    [isApproverNeededAction, isEdit, isEditState]
  );

  return {
    defaultDateOfBirth,
    genderDefaultObject,
    raceDefaultObject,
    occupationDefaultObject,
    schoolDefaultObject,
    IsPersonalParticularsValueUndefined,
    filteredDataOrganisationWithoutMOESchool,
    isMOESchool,
    disabledFields,
  };
};

export const useCandidateContactInformation = ({
  iExamsData,
  candidateProfileDetails,
}: useCreateEditViewCandidateAccountDetailsProps) => {
  const mobileNumberCountryCodeDefaultObject = useMemo(
    () =>
      iExamsData &&
      iExamsData.countryCode.find(
        (obj: IExamMasterCodeItem) =>
          obj.codeValue === candidateProfileDetails?.mobileNumberCountryCode?.toString()
      ),
    [candidateProfileDetails?.mobileNumberCountryCode, iExamsData]
  );

  const telephoneNumberCountryCodeDefaultObject = useMemo(
    () =>
      iExamsData &&
      iExamsData.countryCode.find(
        (obj: IExamMasterCodeItem) =>
          obj.codeValue === candidateProfileDetails?.telephoneCountryCode?.toString()
      ),
    [candidateProfileDetails?.telephoneCountryCode, iExamsData]
  );

  const isContactInformationValueUndefined =
    (candidateProfileDetails?.mobileNumberCountryCode &&
      mobileNumberCountryCodeDefaultObject === undefined) ||
    (candidateProfileDetails?.telephoneCountryCode &&
      telephoneNumberCountryCodeDefaultObject === undefined);

  return {
    mobileNumberCountryCodeDefaultObject,
    telephoneNumberCountryCodeDefaultObject,
    isContactInformationValueUndefined,
  };
};

export const useCandidateNationalityDetails = ({
  iExamsData,
  candidateProfileDetails,
}: useCreateEditViewCandidateAccountDetailsProps) => {
  const defaultNationalityIssueDate =
    (candidateProfileDetails &&
      candidateProfileDetails?.nationalityIssueDate &&
      dayjs
        .unix(candidateProfileDetails?.nationalityIssueDate / 1000)
        .utc()
        .utcOffset(8)
        .format(PAYLOAD_DATE_FORMAT)) ||
    '';

  const citizenshipDefaultObject = useMemo(
    () =>
      iExamsData &&
      iExamsData.citizenship.find(
        (obj) => obj.codeValueDescription === candidateProfileDetails?.citizenship.toUpperCase()
      ),
    [candidateProfileDetails?.citizenship, iExamsData]
  );

  const nationalityDefaultObject = useMemo(
    () =>
      iExamsData &&
      iExamsData.nationality.find(
        (obj) => obj.codeValueDescription === candidateProfileDetails?.nationality.toUpperCase()
      ),
    [candidateProfileDetails?.nationality, iExamsData]
  );

  const prStatusDefaultObject = useMemo(
    () =>
      iExamsData &&
      iExamsData.prStatus.find(
        (obj) => obj.codeValueDescription === candidateProfileDetails?.prStatus.toUpperCase()
      ),
    [candidateProfileDetails?.prStatus, iExamsData]
  );

  const isNationalityDetailsValueUndefined =
    (candidateProfileDetails?.citizenship && citizenshipDefaultObject === undefined) ||
    (candidateProfileDetails?.nationality && nationalityDefaultObject === undefined) ||
    (candidateProfileDetails?.prStatus && prStatusDefaultObject === undefined);

  return {
    defaultNationalityIssueDate,
    citizenshipDefaultObject,
    nationalityDefaultObject,
    prStatusDefaultObject,
    isNationalityDetailsValueUndefined,
  };
};

export const useCandidateNextOfKinDetails = ({
  iExamsData,
  candidateProfileDetails,
}: useCreateEditViewCandidateAccountDetailsProps) => {
  const relationshipDefaultObject = useMemo(
    () =>
      iExamsData &&
      iExamsData.relationship.find(
        (obj) =>
          obj.codeValueDescription.trim().toUpperCase() ===
          candidateProfileDetails?.nextOfKinRelationship?.trim().toUpperCase()
      ),
    [candidateProfileDetails?.nextOfKinRelationship, iExamsData]
  );

  const nextOfKinCountryCodeDefaultObject = useMemo(
    () =>
      iExamsData &&
      iExamsData.countryCode.find(
        (obj: IExamMasterCodeItem) =>
          obj.codeValue === candidateProfileDetails?.nextOfKinContactNumberCountryCode?.toString()
      ),
    [candidateProfileDetails?.nextOfKinContactNumberCountryCode, iExamsData]
  );

  return {
    relationshipDefaultObject,
    nextOfKinCountryCodeDefaultObject,
  };
};

export const useCandidatePayloadValues = ({
  schoolDefaultObject,
  candidateProfileDetails,
  formHook,
  isApproverNeededAction,
  uuid,
  fileKeys,
}: useCreateEditViewCandidateAccountDetailsProps) => {
  const watchSchool = formHook?.watch('school');
  const MOESchoolNameValue = useMemo(
    () =>
      schoolDefaultObject?.name === 'MOE School'
        ? candidateProfileDetails?.school
        : watchSchool?.name,
    [candidateProfileDetails?.school, watchSchool, schoolDefaultObject?.name]
  );

  const MOESchoolCodeValue = useMemo(
    () =>
      schoolDefaultObject?.name === 'MOE School'
        ? candidateProfileDetails?.schoolCode
        : watchSchool?.id,
    [candidateProfileDetails?.schoolCode, watchSchool, schoolDefaultObject?.name]
  );

  const occupationValue = useMemo(
    () =>
      formHook?.getValues('occupation')?.description === undefined
        ? ''
        : formHook?.getValues('occupation')?.description,
    [formHook]
  );

  const payloadRequestObject = useMemo(
    () => ({
      request: isApproverNeededAction
        ? {
            requestorRemarks: formHook?.getValues('remarks'),
            approverUuid: formHook?.getValues('approver')?.code,
            fileKeys,
          }
        : null,
      userUuid: uuid,
      userType: 'CANDIDATE',
    }),
    [fileKeys, formHook, isApproverNeededAction, uuid]
  );

  return {
    MOESchoolNameValue,
    MOESchoolCodeValue,
    occupationValue,
    payloadRequestObject,
  };
};
